import { useEffect } from "react";

interface UseRecalculateMonthlyDataParams {
  lastMonthActiveUsersDuplicate: number;
  selectedYear: string;
  monthlyData: any;
  monthNames: string[];
  isMonthPassed: (monthStr: string) => boolean;
  calculateValues: (
    month: string,
    newValues: any,
    forcePreviousMonthActiveUsers?: number
  ) => any;
  LastData: any[];
  setMonthlyData: (data: any) => void;
}

export const useRecalculateMonthlyData = ({
  lastMonthActiveUsersDuplicate,
  selectedYear,
  monthlyData,
  monthNames,
  isMonthPassed,
  calculateValues,
  LastData,
  setMonthlyData,
}: UseRecalculateMonthlyDataParams) => {
  useEffect(() => {
    // Only recalculate, don't reinitialize from editableRows
    const recalculatedData = { ...monthlyData }; // Keep existing user edits

    for (let i = 0; i < monthNames.length; i++) {
      const month = monthNames[i];
      if (recalculatedData[month]) {
        let previousMonthActiveUsers;

        if (i === 0) {
          // January uses lastMonthActiveUsersDuplicate directly
          previousMonthActiveUsers = lastMonthActiveUsersDuplicate;
        } else {
          // Check if month has passed to determine which value to use
          const monthNum = i + 1;
          const paddedMonth = monthNum.toString().padStart(2, "0");
          const monthStr = `${selectedYear}-${paddedMonth}`;
          const isPassed = isMonthPassed(monthStr);

          if (isPassed) {
            // For passed months, use lastMonthActiveUsersDuplicate or LastData
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth();
            const currentDay = currentDate.getDate();
            const isEarlyQ3 = currentMonth === 6 && currentDay <= 15;

            if (isEarlyQ3 && i === 6) {
              // July
              const juneData = LastData.find((entry: any) => {
                const [year, month] = entry.month.split("-").map(Number);
                return month === 6 && year === Number(selectedYear);
              });
              previousMonthActiveUsers =
                juneData?.active_users || lastMonthActiveUsersDuplicate;
            } else {
              previousMonthActiveUsers = lastMonthActiveUsersDuplicate;
            }
          } else {
            // For future months, use previous month's calculated activeUsers
            const prevMonth = monthNames[i - 1];
            previousMonthActiveUsers = recalculatedData[prevMonth]?.activeUsers ?? lastMonthActiveUsersDuplicate;
          }
        }

        // Recalculate with existing data (preserves user edits)
        recalculatedData[month] = calculateValues(
          month,
          recalculatedData[month], // Use existing values, not editableRows
          previousMonthActiveUsers
        );
      }
    }

    setMonthlyData(recalculatedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMonthActiveUsersDuplicate, selectedYear]);
};
